import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Copy, ShieldBan, Upload, Power, Globe, ShieldOff, Save, Eye, MousePointerClick, ClipboardCheck, ChevronDown } from "lucide-react";
import api, { BACKEND_URL, formatApiError } from "@/lib/api";

const STATUSES = ["new", "contacted", "closed"];

const Table = ({ head, children, testid }) => (
  <div className="border border-[#DFE5DC] bg-white overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm min-w-[760px]">
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h} className="text-left font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#5C6B62] p-3 border-b border-[#DFE5DC]">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const Empty = ({ colSpan, label }) => (
  <tr>
    <td colSpan={colSpan} className="p-10 text-center font-mono2 text-xs uppercase tracking-[0.2em] text-[#8A978D]">
      {label}
    </td>
  </tr>
);

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

const StatusSelect = ({ value, onChange, testid }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    className="bg-white border border-[#DFE5DC] text-xs font-mono2 px-2 py-1.5 text-[#0F1F1A] focus:outline-none focus:border-[#0C6B4F]"
  >
    {STATUSES.map((s) => (
      <option key={s} value={s}>{s}</option>
    ))}
  </select>
);

const DeleteBtn = ({ onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="text-[#8A978D] hover:text-[#D92D20] transition-colors duration-300" aria-label="Delete">
    <Trash2 className="w-4 h-4" />
  </button>
);

export function LeadsPanel({ onChange }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/leads").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const setStatus = (id, status) =>
    api.patch(`/admin/leads/${id}`, { status }).then(() => { load(); onChange?.(); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  const remove = (id) =>
    api.delete(`/admin/leads/${id}`).then(() => { toast.success("Lead deleted"); load(); onChange?.(); });

  return (
    <Table testid="leads-table" head={["Date", "Name", "Contact", "Service", "Amount", "State / ZIP", "IP", "Status", ""]}>
      {rows === null && <Empty colSpan={9} label="Loading…" />}
      {rows?.length === 0 && <Empty colSpan={9} label="No leads yet" />}
      {rows?.map((r) => (
        <tr key={r.id} className="hover:bg-[#EDF2EA] transition-colors duration-200" data-testid={`lead-row-${r.id}`}>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#5C6B62] whitespace-nowrap">{fmt(r.created_at)}</td>
          <td className="p-3 border-b border-[#DFE5DC]">{r.first_name} {r.last_name}</td>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#5C6B62]">{r.email}<br />{r.phone}</td>
          <td className="p-3 border-b border-[#DFE5DC] text-xs">{r.service}</td>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs">{r.amount}</td>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#5C6B62]">{[r.state, r.zip].filter(Boolean).join(" ") || "—"}</td>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#5C6B62]">{r.ip}</td>
          <td className="p-3 border-b border-[#DFE5DC]">
            <StatusSelect value={r.status} onChange={(s) => setStatus(r.id, s)} testid={`lead-status-${r.id}`} />
          </td>
          <td className="p-3 border-b border-[#DFE5DC]">
            <DeleteBtn onClick={() => remove(r.id)} testid={`lead-delete-${r.id}`} />
          </td>
        </tr>
      ))}
    </Table>
  );
}

export function EnquiriesPanel({ onChange }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/enquiries").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const setStatus = (id, status) =>
    api.patch(`/admin/enquiries/${id}`, { status }).then(() => { load(); onChange?.(); });
  const remove = (id) =>
    api.delete(`/admin/enquiries/${id}`).then(() => { toast.success("Enquiry deleted"); load(); onChange?.(); });

  return (
    <Table testid="enquiries-table" head={["Date", "Name", "Email", "Message", "Status", ""]}>
      {rows === null && <Empty colSpan={6} label="Loading…" />}
      {rows?.length === 0 && <Empty colSpan={6} label="No enquiries yet" />}
      {rows?.map((r) => (
        <tr key={r.id} className="hover:bg-[#EDF2EA] transition-colors duration-200" data-testid={`enquiry-row-${r.id}`}>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#5C6B62] whitespace-nowrap">{fmt(r.created_at)}</td>
          <td className="p-3 border-b border-[#DFE5DC]">{r.first_name} {r.last_name}</td>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#5C6B62]">{r.email}</td>
          <td className="p-3 border-b border-[#DFE5DC] text-xs text-[#5C6B62] max-w-[280px]"><span className="line-clamp-2">{r.message}</span></td>
          <td className="p-3 border-b border-[#DFE5DC]">
            <StatusSelect value={r.status} onChange={(s) => setStatus(r.id, s)} testid={`enquiry-status-${r.id}`} />
          </td>
          <td className="p-3 border-b border-[#DFE5DC]">
            <DeleteBtn onClick={() => remove(r.id)} testid={`enquiry-delete-${r.id}`} />
          </td>
        </tr>
      ))}
    </Table>
  );
}

const EMPTY_OFFER = {
  name: "", slug: "", category: "credit-builder", badge: "", headline: "",
  description: "", features: "", cta_label: "Learn More", destination_url: "", image_url: "", active: true,
};

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export function OffersPanel({ onChange }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY_OFFER);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/admin/offers").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v, ...(k === "name" && !editing ? { slug: slugify(v) } : {}) }));
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Banner uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/admin/offers/${editing}`, payload);
        toast.success("Offer updated");
      } else {
        await api.post("/admin/offers", payload);
        toast.success("Offer created");
      }
      setForm(EMPTY_OFFER);
      setEditing(null);
      load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const edit = (o) => {
    setEditing(o.id);
    setForm({
      name: o.name, slug: o.slug, category: o.category, badge: o.badge || "", headline: o.headline || "",
      description: o.description || "", features: (o.features || []).join("\n"),
      cta_label: o.cta_label || "Learn More", destination_url: o.destination_url || "",
      image_url: o.image_url || "", active: o.active,
    });
  };

  const toggle = (o) =>
    api.put(`/admin/offers/${o.id}`, {
      name: o.name, slug: o.slug, category: o.category, badge: o.badge || "", headline: o.headline || "",
      description: o.description || "", features: o.features || [], cta_label: o.cta_label || "Learn More",
      destination_url: o.destination_url || "", image_url: o.image_url || "", active: !o.active, sort_order: o.sort_order || 0,
    }).then(() => { load(); onChange?.(); });

  const remove = (id) =>
    api.delete(`/admin/offers/${id}`).then(() => { toast.success("Offer deleted"); load(); onChange?.(); });

  const copy = (slug) => {
    navigator.clipboard?.writeText(`${BACKEND_URL}/api/t/${slug}`).then(() => toast.success("Tracking link copied"));
  };

  return (
    <div className="flex flex-col gap-8">
      <form onSubmit={submit} className="border border-[#DFE5DC] bg-[#FFFFFF] p-6 grid md:grid-cols-3 gap-4" data-testid="offer-form">
        <p className="md:col-span-3 font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A978D]">
          {editing ? "Edit offer" : "Add new offer"}
        </p>
        <input className="a-input" placeholder="Partner name" required value={form.name} onChange={set("name")} data-testid="offer-name" />
        <input className="a-input font-mono2" placeholder="tracking-slug" required value={form.slug} onChange={set("slug")} data-testid="offer-slug" />
        <select className="a-input" value={form.category} onChange={set("category")} data-testid="offer-category">
          <option value="credit-builder">Credit Builder</option>
          <option value="identity-protection">Identity Protection</option>
        </select>
        <input className="a-input" placeholder="Badge (e.g. Editor's Choice)" value={form.badge} onChange={set("badge")} data-testid="offer-badge" />
        <input className="a-input" placeholder="Headline" value={form.headline} onChange={set("headline")} data-testid="offer-headline" />
        <input className="a-input" placeholder="CTA label" value={form.cta_label} onChange={set("cta_label")} data-testid="offer-cta-label" />
        <textarea className="a-input md:col-span-2 min-h-[80px]" placeholder="Description" value={form.description} onChange={set("description")} data-testid="offer-description" />
        <textarea className="a-input min-h-[80px] font-mono2 text-xs" placeholder={"Features — one per line"} value={form.features} onChange={set("features")} data-testid="offer-features" />
        <input className="a-input md:col-span-2 font-mono2" placeholder="Destination URL (https://partner-link…)" value={form.destination_url} onChange={set("destination_url")} data-testid="offer-destination" />
        <label className="flex items-center gap-3 text-xs text-[#5C6B62] uppercase tracking-[0.15em]">
          <input type="checkbox" checked={form.active} onChange={set("active")} data-testid="offer-active" className="accent-[#0C6B4F] w-4 h-4" />
          Live on site
        </label>
        <div className="md:col-span-3 flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1 w-full">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A978D] mb-2">Banner image (optional)</p>
            <input className="a-input font-mono2" placeholder="Paste image URL or upload →" value={form.image_url} onChange={set("image_url")} data-testid="offer-image-url" />
          </div>
          <label className="inline-flex items-center gap-2 border border-[#DFE5DC] text-xs uppercase tracking-[0.15em] px-5 py-3.5 rounded-full text-[#5C6B62] hover:text-[#0C6B4F] hover:border-[#0C6B4F] cursor-pointer transition-colors duration-300 shrink-0" data-testid="offer-upload-button">
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload Image"}
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="offer-upload-input" />
          </label>
          {form.image_url && (
            <div className="w-32 h-[72px] bg-white border border-[#DFE5DC] rounded-xl flex items-center justify-center p-1.5 overflow-hidden shrink-0">
              <img
                src={form.image_url.startsWith("/") ? `${BACKEND_URL}${form.image_url}` : form.image_url}
                alt="Banner preview"
                className="max-w-full max-h-full object-contain"
                data-testid="offer-image-preview"
              />
            </div>
          )}
        </div>
        <div className="md:col-span-3 flex gap-3">
          <button type="submit" data-testid="offer-submit" className="bg-[#0C6B4F] text-white text-xs font-semibold uppercase tracking-[0.2em] px-8 py-3 rounded-full hover:bg-[#C8E846] active:scale-[0.98] transition-[background-color,transform] duration-300">
            {editing ? "Save Changes" : "Create Offer"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY_OFFER); }} data-testid="offer-cancel-edit" className="border border-[#DFE5DC] text-xs uppercase tracking-[0.2em] px-6 py-3 rounded-full text-[#5C6B62] hover:text-[#0F1F1A] transition-colors duration-300">
              Cancel
            </button>
          )}
        </div>
      </form>

      <Table testid="offers-table" head={["Partner", "Category", "Tracking Link", "Clicks", "Live", "Actions"]}>
        {rows === null && <Empty colSpan={6} label="Loading…" />}
        {rows?.length === 0 && <Empty colSpan={6} label="No offers yet" />}
        {rows?.map((o) => (
          <tr key={o.id} className="hover:bg-[#EDF2EA] transition-colors duration-200" data-testid={`offer-row-${o.slug}`}>
            <td className="p-3 border-b border-[#DFE5DC]">{o.name}<br /><span className="font-mono2 text-xs text-[#8A978D]">{o.slug}</span></td>
            <td className="p-3 border-b border-[#DFE5DC] text-xs text-[#5C6B62]">{o.category}</td>
            <td className="p-3 border-b border-[#DFE5DC]">
              <span className="inline-flex items-center gap-2 font-mono2 text-xs text-[#5C6B62]">
                /api/t/{o.slug}
                <button onClick={() => copy(o.slug)} data-testid={`offer-copy-${o.slug}`} className="text-[#8A978D] hover:text-[#0C6B4F] transition-colors duration-300" aria-label="Copy tracking link">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </span>
            </td>
            <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-[#0C6B4F]" data-testid={`offer-clicks-${o.slug}`}>{o.clicks}</td>
            <td className="p-3 border-b border-[#DFE5DC]">
              <button
                onClick={() => toggle(o)}
                data-testid={`offer-toggle-${o.slug}`}
                className={`font-mono2 text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full border transition-colors duration-300 ${o.active ? "border-[#0C6B4F]/50 text-[#0C6B4F]" : "border-[#DFE5DC] text-[#8A978D]"}`}
              >
                {o.active ? "Live" : "Off"}
              </button>
            </td>
            <td className="p-3 border-b border-[#DFE5DC] whitespace-nowrap">
              <button onClick={() => edit(o)} data-testid={`offer-edit-${o.slug}`} className="text-xs uppercase tracking-[0.15em] text-[#5C6B62] hover:text-[#0C6B4F] transition-colors duration-300 mr-4">Edit</button>
              <DeleteBtn onClick={() => remove(o.id)} testid={`offer-delete-${o.slug}`} />
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

const Toggle = ({ on, onChange, testid }) => (
  <button
    type="button"
    onClick={() => onChange(!on)}
    data-testid={testid}
    className={`w-11 h-6 rounded-full relative shrink-0 transition-colors duration-300 ${on ? "bg-[#0C6B4F]" : "bg-[#DFE5DC]"}`}
    aria-pressed={on}
  >
    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-[left] duration-300 ${on ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

export function AccessControlPanel() {
  const [form, setForm] = useState({ enabled: false, block_india: false, block_vpn: false });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/access-control").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/access-control", form);
      toast.success("Access control saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    { key: "enabled", icon: Power, title: "Enable access restrictions", desc: "Master toggle. When OFF, no visitors are blocked — the other rules below are ignored.", testid: "toggle-enabled" },
    { key: "block_india", icon: Globe, title: "Block Indian IP addresses", desc: 'Visitors with country code IN will see the "Access Restricted" page.', testid: "toggle-block-india" },
    { key: "block_vpn", icon: ShieldOff, title: "Block VPN / proxy / datacenter IPs", desc: "Detects known VPN providers, proxies, Tor, and datacenter/hosting IPs (AWS, GCP, Azure, DigitalOcean, OVH, Hetzner, etc.).", testid: "toggle-block-vpn" },
  ];

  return (
    <div className="max-w-3xl" data-testid="access-control-panel">
      <h2 className="font-display text-3xl text-[#0F1F1A] mb-2">Access Control</h2>
      <p className="text-[#5C6B62] text-sm mb-8">Block visitors by country or VPN/proxy. Admin pages are never blocked.</p>

      <div className="flex flex-col gap-4 mb-6">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center gap-5 bg-white border border-[#DFE5DC] rounded-2xl p-5" data-testid={`access-row-${r.key}`}>
            <div className="w-11 h-11 rounded-xl bg-[#EDF2EA] flex items-center justify-center shrink-0">
              <r.icon className="w-5 h-5 text-[#0C6B4F]" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-[#0F1F1A]">{r.title}</p>
              <p className="text-xs text-[#5C6B62] mt-1 leading-relaxed">{r.desc}</p>
            </div>
            <Toggle on={form[r.key]} onChange={(v) => setForm({ ...form, [r.key]: v })} testid={r.testid} />
          </div>
        ))}
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="access-save-button"
        className="inline-flex items-center gap-2 bg-[#0C6B4F] text-white text-xs font-semibold uppercase tracking-[0.15em] px-6 py-3.5 rounded-full hover:bg-[#0A5C43] active:scale-[0.98] disabled:opacity-60 transition-[background-color,transform,opacity] duration-300 mb-8"
      >
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
      </button>

      <div className="bg-white border border-[#DFE5DC] rounded-2xl p-6" data-testid="access-notes">
        <p className="font-semibold text-sm text-[#0F1F1A] mb-3">Notes</p>
        <ul className="flex flex-col gap-2 text-xs text-[#5C6B62] leading-relaxed list-disc pl-4">
          <li>IP lookups are cached for 24 hours per unique visitor to reduce API calls.</li>
          <li>The admin panel (/admin/*) is <strong className="text-[#0F1F1A]">never</strong> blocked — you can always reach it.</li>
          <li>Private / local IPs (server-side, dev) are never blocked.</li>
          <li>If the geo lookup fails, visitors are allowed (fail-open).</li>
          <li>Manually blocked IPs (Blocked IPs tab) always apply, even when the master toggle is off.</li>
        </ul>
      </div>
    </div>
  );
}

export function SubscribersPanel({ onChange }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/subscribers").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const remove = (id) =>
    api.delete(`/admin/subscribers/${id}`).then(() => { toast.success("Subscriber removed"); load(); onChange?.(); });

  return (
    <Table testid="subscribers-table" head={["Joined", "Name", "Email", "IP", ""]}>
      {rows === null && <Empty colSpan={5} label="Loading…" />}
      {rows?.length === 0 && <Empty colSpan={5} label="No VIP subscribers yet" />}
      {rows?.map((r) => (
        <tr key={r.id} className="hover:bg-[#EDF2EA] transition-colors duration-200" data-testid={`subscriber-row-${r.id}`}>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#5C6B62] whitespace-nowrap">{fmt(r.created_at)}</td>
          <td className="p-3 border-b border-[#DFE5DC]">{r.first_name || "—"}</td>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#5C6B62]">{r.email}</td>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#5C6B62]">{r.ip}</td>
          <td className="p-3 border-b border-[#DFE5DC]">
            <DeleteBtn onClick={() => remove(r.id)} testid={`subscriber-delete-${r.id}`} />
          </td>
        </tr>
      ))}
    </Table>
  );
}

export function JourneysPanel() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/admin/journeys").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  const iconFor = (t) => (t === "pageview" ? Eye : t === "form_submit" ? ClipboardCheck : MousePointerClick);

  if (rows === null)
    return <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#8A978D] p-10">Loading…</p>;
  if (!rows.length)
    return (
      <div className="border border-[#DFE5DC] bg-white rounded-2xl p-10 text-center font-mono2 text-xs uppercase tracking-[0.2em] text-[#8A978D]" data-testid="journeys-empty">
        No visitor journeys yet
      </div>
    );

  return (
    <div className="flex flex-col gap-3" data-testid="journeys-panel">
      {rows.map((s) => {
        const isOpen = open === s.session;
        return (
          <div key={s.session} className="border border-[#DFE5DC] bg-white rounded-2xl overflow-hidden" data-testid={`journey-card-${s.session.slice(0, 8)}`}>
            <button
              className="w-full flex flex-wrap items-center gap-x-6 gap-y-2 p-4 text-left hover:bg-[#EDF2EA] transition-colors duration-200"
              onClick={() => setOpen(isOpen ? null : s.session)}
              data-testid={`journey-toggle-${s.session.slice(0, 8)}`}
            >
              <span className="font-mono2 text-xs text-[#0C6B4F]">#{s.session.slice(0, 8)}</span>
              <span className={`text-sm font-semibold ${s.customer ? "text-[#0F1F1A]" : "text-[#8A978D] font-normal"}`} data-testid={`journey-customer-${s.session.slice(0, 8)}`}>
                {s.customer || "Anonymous visitor"}
              </span>
              <span className="font-mono2 text-xs text-[#5C6B62]">{s.ip}</span>
              <span className="font-mono2 text-xs text-[#8A978D]">{fmt(s.started)}</span>
              <span className="font-mono2 text-xs text-[#8A978D]">{s.count} events</span>
              {s.submitted ? (
                <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full border border-[#0C6B4F]/50 text-[#0C6B4F]">Form submitted</span>
              ) : (
                <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full border border-[#DFE5DC] text-[#8A978D]">Browsed</span>
              )}
              <ChevronDown className={`w-4 h-4 ml-auto text-[#8A978D] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <div className="border-t border-[#DFE5DC] px-4 py-2">
                {s.events.map((e) => {
                  const Icon = iconFor(e.type);
                  return (
                    <div key={e.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 py-2.5 border-b border-[#EDF2EA] last:border-0 text-xs">
                      <span className="font-mono2 text-[#8A978D] shrink-0">{new Date(e.ts).toLocaleTimeString()}</span>
                      <Icon className="w-3.5 h-3.5 text-[#0C6B4F] shrink-0 self-center" />
                      <span className="font-mono2 text-[#0F1F1A]">{e.page}</span>
                      {e.target && <span className="text-[#5C6B62]">→ {e.target}</span>}
                      <span className="font-mono2 text-[10px] uppercase text-[#8A978D] ml-auto">{e.type.replace("_", " ")}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ClicksPanel() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/clicks").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  return (
    <Table testid="clicks-table" head={["Time", "Offer", "IP", "Referrer", "User Agent"]}>
      {rows === null && <Empty colSpan={5} label="Loading…" />}
      {rows?.length === 0 && <Empty colSpan={5} label="No tracked clicks yet" />}
      {rows?.map((c) => (
        <tr key={c.id} className="hover:bg-[#EDF2EA] transition-colors duration-200">
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#5C6B62] whitespace-nowrap">{fmt(c.ts)}</td>
          <td className="p-3 border-b border-[#DFE5DC] text-xs">{c.offer_name}</td>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#0C6B4F]">{c.ip}</td>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#5C6B62] max-w-[200px] truncate">{c.referrer || "—"}</td>
          <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#8A978D] max-w-[220px] truncate">{c.user_agent}</td>
        </tr>
      ))}
    </Table>
  );
}

export function IpsPanel({ onChange }) {
  const [rows, setRows] = useState(null);
  const [ip, setIp] = useState("");
  const [reason, setReason] = useState("");
  const load = () => api.get("/admin/blocked-ips").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blocked-ips", { ip, reason });
      toast.success(`${ip} blocked`);
      setIp("");
      setReason("");
      load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const unblock = (target) =>
    api.delete(`/admin/blocked-ips/${encodeURIComponent(target)}`).then(() => { toast.success(`${target} unblocked`); load(); onChange?.(); });

  return (
    <div className="flex flex-col gap-8">
      <form onSubmit={submit} className="border border-[#DFE5DC] bg-[#FFFFFF] p-6 grid md:grid-cols-[1fr_2fr_auto] gap-4 items-end" data-testid="block-ip-form">
        <div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A978D] mb-2">IP Address</p>
          <input className="a-input font-mono2" placeholder="203.0.113.10" required value={ip} onChange={(e) => setIp(e.target.value)} data-testid="block-ip-input" />
        </div>
        <div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A978D] mb-2">Reason (optional)</p>
          <input className="a-input" placeholder="Spam submissions" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="block-ip-reason" />
        </div>
        <button type="submit" data-testid="block-ip-submit" className="inline-flex items-center gap-2 bg-[#D92D20] text-white text-xs font-semibold uppercase tracking-[0.2em] px-6 py-3.5 rounded-full hover:opacity-90 active:scale-[0.98] transition-[opacity,transform] duration-300">
          <ShieldBan className="w-4 h-4" /> Block IP
        </button>
      </form>

      <Table testid="blocked-ips-table" head={["IP Address", "Reason", "Blocked At", ""]}>
        {rows === null && <Empty colSpan={4} label="Loading…" />}
        {rows?.length === 0 && <Empty colSpan={4} label="No blocked IPs" />}
        {rows?.map((r) => (
          <tr key={r.ip} className="hover:bg-[#EDF2EA] transition-colors duration-200" data-testid={`blocked-ip-row-${r.ip}`}>
            <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#D92D20]">{r.ip}</td>
            <td className="p-3 border-b border-[#DFE5DC] text-xs text-[#5C6B62]">{r.reason || "—"}</td>
            <td className="p-3 border-b border-[#DFE5DC] font-mono2 text-xs text-[#5C6B62]">{fmt(r.created_at)}</td>
            <td className="p-3 border-b border-[#DFE5DC]">
              <button onClick={() => unblock(r.ip)} data-testid={`unblock-${r.ip}`} className="text-xs uppercase tracking-[0.15em] text-[#5C6B62] hover:text-[#0C6B4F] transition-colors duration-300">
                Unblock
              </button>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}
